/**
 * Coach Engine — Shared Analyzer Utilities
 *
 * Pure helpers used by multiple analyzers. Kept here (not duplicated in
 * each analyzer) so metrics stay consistent app-wide. No React, no DOM.
 */

// parseHM is sourced from the central timeService (which adds 12-hour AM/PM
// support the previous local copy silently lacked). Imported for in-file use
// AND re-exported so existing callers that import from this module keep
// working without an import-path migration.
import { parseHM } from '../timeService.js';
export { parseHM };

/**
 * Compute net P&L for each trade using the provided helper (keeps account-
 * specific commission math consistent with the rest of the app).
 *
 * @param {Array} trades
 * @param {Function} getTradeNetPnL - Takes (trade, account) → { netPnl }
 * @param {Function} getAccountById - Takes (accountId) → account|null
 * @returns {Array<{trade, netPnl}>}
 */
export const withNetPnl = (trades, getTradeNetPnL, getAccountById) => {
  if (!Array.isArray(trades)) return [];
  return trades.map(t => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const result = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    const netPnl = isFinite(result?.netPnl) ? result.netPnl : 0;
    return { trade: t, netPnl };
  });
};

/**
 * Summarize win/loss counts + total P&L.
 *
 * **Closed-only semantics.** Live (`trade.isOpen === true`) trades have
 * unrealized mark-to-market P&L, which is NOT a realized outcome — counting
 * them as wins/losses produces premature "this is failing" claims while the
 * user is still in the trade (panic-exit risk). All four core fields
 * (`wins`, `losses`, `total`, `winRate`) reflect closed trades only.
 *
 * Live trades are exposed via the additive `pending` count + `closedTotal`
 * denominator so analyzers can gate negative-outcome observations on
 * "all trades closed" before firing.
 *
 * @param {Array<{trade, netPnl}>} enriched
 * @returns {{wins: number, losses: number, total: number, winRate: number, pending: number, closedTotal: number}}
 *   `wins`/`losses`/`total`/`winRate` cover closed trades only;
 *   `pending` is the count of live trades excluded;
 *   `closedTotal` is the denominator behind `winRate` (= closed-trade count).
 */
export const summarizePnl = (enriched) => {
  const arr = Array.isArray(enriched) ? enriched : [];
  const closed = arr.filter(x => x?.trade?.isOpen !== true);
  const pending = arr.length - closed.length;
  const wins = closed.filter(x => x.netPnl > 0).length;
  const losses = closed.filter(x => x.netPnl < 0).length;
  const total = closed.reduce((s, x) => s + (Number.isFinite(x.netPnl) ? x.netPnl : 0), 0);
  const winRate = closed.length > 0 ? wins / closed.length : 0;
  return { wins, losses, total, winRate, pending, closedTotal: closed.length };
};

/**
 * Compute average R-multiple for a set of trades, ignoring zero/non-numeric values.
 *
 * @param {Array} trades
 * @returns {{avgR: number|null, rValues: number[]}}
 */
export const computeR = (trades) => {
  const rValues = (trades || [])
    .map(t => parseFloat(t.rMultiple))
    .filter(r => isFinite(r) && r !== 0);
  const avgR = rValues.length ? rValues.reduce((s, r) => s + r, 0) / rValues.length : null;
  return { avgR, rValues };
};

/**
 * Count occurrences of array-field values across trades, deduped per trade.
 * E.g. count how many trades had each confluence id.
 *
 * @param {Array} trades
 * @param {string} field - Field name on the trade (e.g. 'confluences', 'mistakes', 'tags')
 * @returns {Map<string, number>}
 */
export const countAcrossTrades = (trades, field) => {
  const counts = new Map();
  (trades || []).forEach(t => {
    const arr = t?.[field];
    if (!Array.isArray(arr)) return;
    const seen = new Set();
    arr.forEach(val => {
      if (!val || seen.has(val)) return;
      seen.add(val);
      counts.set(val, (counts.get(val) || 0) + 1);
    });
  });
  return counts;
};

/**
 * Sort trades chronologically by entry time. Returns a new array.
 *
 * @param {Array<{trade, netPnl}>|Array} trades - Either enriched or raw
 * @returns {Array}
 */
export const sortByEntryTime = (trades) => {
  if (!Array.isArray(trades)) return [];
  const getTime = (t) => {
    const trade = t.trade || t;
    return parseHM(trade.entryTime) ?? 0;
  };
  return [...trades].sort((a, b) => getTime(a) - getTime(b));
};

/**
 * Minutes between two trades based on entry times on the same day.
 * Returns null if times can't be parsed.
 *
 * @param {Object} earlier
 * @param {Object} later
 * @returns {number|null}
 */
export const minutesBetween = (earlier, later) => {
  const a = parseHM(earlier?.entryTime);
  const b = parseHM(later?.entryTime);
  if (a === null || b === null) return null;
  return b - a;
};

/**
 * Group trades by a key function.
 *
 * @param {Array} trades
 * @param {Function} keyFn - (trade) => string
 * @returns {Map<string, Array>}
 */
export const groupBy = (trades, keyFn) => {
  const groups = new Map();
  (trades || []).forEach(t => {
    const k = keyFn(t);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(t);
  });
  return groups;
};

/**
 * Collapse trades that represent ONE trader decision fanned across multiple
 * prop-firm accounts into a single representative event.
 *
 * Many prop traders run 3-7 accounts simultaneously. When they decide to
 * enter a setup, the order routes the same fill across every account with
 * per-account quantity / ATM parameters. Behavioral analyzers (tilt,
 * revenge sizing, loss-chain counting, rapid re-entry) MUST treat that
 * fan-out as ONE decision — otherwise they fabricate phantom tilt patterns
 * out of a perfectly disciplined single entry.
 *
 * Dedup key: `symbol|direction|entryTime|entryPrice` — these uniquely
 * identify a trading decision. Quantity, accountId, ATM strategy, stop
 * loss differ per-leg and are intentionally excluded from the key.
 *
 * Guard against false collapses:
 *   - A group is only collapsed when it contains >= 2 distinct accountIds.
 *     Same-account "duplicates" (manual double-entry, sync hiccup) are
 *     NOT collapsed — those represent real over-trading risk that should
 *     stay visible.
 *
 * Behavior of returned items:
 *   - Single-trade groups pass through unchanged.
 *   - Collapsed groups return ONE enriched item:
 *       - `trade`: the leg with the largest quantity (closest to "the
 *         canonical leg"; arbitrary but stable tiebreaker).
 *       - `netPnl`: SUM across all legs — that's what the trader's
 *         account balances actually changed by, and the right answer
 *         for "was this decision a loss?".
 *       - `_collapsedFrom`: original group size (debugging aid).
 *
 * Works on either raw trades or enriched `{trade, netPnl}` items.
 * Returns the same shape it received.
 *
 * @param {Array} items - Either trades or enriched-trade objects.
 * @returns {Array} Same shape, with multi-account fan-outs collapsed.
 */
export const collapseMultiAccountDecisions = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  // Detect input shape — enriched items have `.trade`; raw trades don't.
  // Build a unified accessor so the rest of the function doesn't branch.
  const isEnriched = items[0] && typeof items[0] === 'object' && 'trade' in items[0];
  const getTrade = (item) => (isEnriched ? item.trade : item);

  // Build a dedup key from the four DECISION-identifying fields. Anything
  // a same-decision fan-out would share goes in; per-leg differences go out.
  const decisionKey = (t) => {
    if (!t) return '';
    const entryNum = parseFloat(t.entry);
    const entryRounded = Number.isFinite(entryNum) ? Math.round(entryNum * 100) / 100 : '';
    return [
      String(t.symbol || '').toUpperCase(),
      String(t.direction || '').toLowerCase(),
      t.entryTime || '',
      entryRounded,
    ].join('|');
  };

  const groups = new Map();
  for (const item of items) {
    const t = getTrade(item);
    if (!t) continue;
    const k = decisionKey(t);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(item);
  }

  const out = [];
  for (const group of groups.values()) {
    // Single trade → pass through. Most groups will be size 1; this is
    // the common case and the cheapest exit.
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }

    // Multiple trades sharing a decision key. Only collapse if they're
    // genuinely across DIFFERENT accounts — otherwise the user has
    // multiple entries on the same account (likely a real issue worth
    // surfacing, not a fan-out to hide).
    const uniqueAccounts = new Set(
      group.map((item) => String(getTrade(item)?.accountId || ''))
    );
    if (uniqueAccounts.size <= 1) {
      for (const item of group) out.push(item);
      continue;
    }

    // Genuine multi-account fan-out — collapse.
    // Representative leg: the one with the largest quantity.
    const sortedByQty = [...group].sort((a, b) => {
      const qa = parseInt(getTrade(a)?.quantity) || 0;
      const qb = parseInt(getTrade(b)?.quantity) || 0;
      return qb - qa;
    });
    const rep = sortedByQty[0];
    const repTrade = getTrade(rep);

    if (isEnriched) {
      const summedPnl = group.reduce((s, item) => {
        const n = Number.isFinite(item.netPnl) ? item.netPnl : 0;
        return s + n;
      }, 0);
      out.push({ trade: repTrade, netPnl: summedPnl, _collapsedFrom: group.length });
    } else {
      // Raw trade input — attach the collapse count as a non-functional
      // marker so callers can introspect if desired. Original fields
      // untouched.
      out.push({ ...repTrade, _collapsedFrom: group.length });
    }
  }

  // Preserve approximate chronological order — out-of-order collapsed
  // groups should still sort cleanly. Stable sort by entryTime keeps
  // determinism for tests + analyzer comparisons.
  out.sort((a, b) => {
    const ta = parseHM(getTrade(a)?.entryTime) ?? 0;
    const tb = parseHM(getTrade(b)?.entryTime) ?? 0;
    return ta - tb;
  });
  return out;
};

/**
 * Format a dollar amount with a sign and no currency symbol.
 * Used inside titles/details to stay terse.
 */
export const fmt$ = (n) => {
  const v = Number(n) || 0;
  return `${v >= 0 ? '+' : ''}${v.toFixed(0)}`;
};

/**
 * Build a stable signature for a session + its trades. Used by the
 * real-time auto-refresh layer to cheaply detect whether anything an
 * analyzer cares about has actually changed — so we don't re-run the
 * full analyzer bank on identity-stable re-renders.
 *
 * Covers: session fields that feed observations (thesis, rating,
 * lessons) + trade fields analyzers read (pnl, direction, symbol,
 * account, times, confluences, mistakes, tags, R). Trade arrays are
 * sorted by id first so ordering changes don't force spurious refreshes.
 *
 * setupDetails: includes the recommendedConfluences arrays for setups
 * present in this session so confluenceConsensus's "is this already
 * pinned?" filter triggers a refresh on pin/unpin. Without this, clicking
 * "Pin to X" updates setupDetails but the signature stays stable → the
 * cached observation persists with the now-stale Pin button.
 *
 * @param {Object} session
 * @param {Array}  trades         Trades belonging to this session
 * @param {Object} [setupDetails] Setup library — optional for backward compat
 * @returns {string}
 */
export const buildSessionSignature = (session, trades, setupDetails = null) => {
  if (!session || !session.id) return '';
  const sessionPart = [
    session.id,
    session.thesis || '',
    session.rating || '',
    session.lessons || ''
  ].join('|');
  const tradeArr = Array.isArray(trades) ? trades.slice() : [];
  tradeArr.sort((a, b) => String(a?.id || '').localeCompare(String(b?.id || '')));
  const tradesPart = tradeArr.map(t => {
    if (!t) return '';
    const confs    = Array.isArray(t.confluences) ? t.confluences.slice().sort().join(',') : '';
    const mists    = Array.isArray(t.mistakes)    ? t.mistakes.slice().sort().join(',')    : '';
    const tags     = Array.isArray(t.tags)        ? t.tags.slice().sort().join(',')        : '';
    return [
      t.id, t.pnl, t.qty, t.direction, t.symbol, t.accountId,
      t.entryTime, t.exitTime, t.rMultiple,
      confs, mists, tags
    ].join(':');
  }).join('|');

  // Setup-details slice — only the setups that appear in THIS session's
  // trades, and only the recommendedConfluences field (the only one the
  // session-scope analyzers currently read). Sorted for stability.
  let setupPart = '';
  if (setupDetails && typeof setupDetails === 'object') {
    const setupNames = new Set();
    for (const t of tradeArr) {
      const s = (t?.setup || '').trim();
      if (s) setupNames.add(s);
    }
    const slices = [];
    for (const name of [...setupNames].sort()) {
      const rec = Array.isArray(setupDetails[name]?.recommendedConfluences)
        ? setupDetails[name].recommendedConfluences.slice().sort().join(',')
        : '';
      slices.push(`${name}:${rec}`);
    }
    setupPart = slices.join('|');
  }

  return `${sessionPart}||${tradesPart}||${setupPart}`;
};
